import React from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import HeaderThin from 'app/components/layout/HeaderThin';
import SectionGroup from 'app/components/general/SectionGroup';
import SchemaForm from 'app/components/general/SchemaForm';
import OrganisationFeesTable from 'app/components/organisationFees/OrganisationFeesTable';
import NotificationConfigsTable from 'app/components/notificationConfigs/NotificationConfigsTable';

import * as competitionActions from 'app/ducks/competitions/actions';
import * as organisationFeesActions from 'app/ducks/organisationFees/actions';
import * as notificationConfigActions from 'app/ducks/notificationConfigs/actions';
import * as loadingSelectors from 'app/ducks/loading/selectors';
import * as competitionSelectors from 'app/ducks/competitions/selectors';
import * as organisationSelectors from 'app/ducks/organisations/selectors';
import * as organisationFeesSelectors from 'app/ducks/organisationFees/selectors';
import * as notificationConfigsSelectors from 'app/ducks/notificationConfigs/selectors';
import Button from 'app/components/general/Button';
import ModalConfirm from 'app/components/general/ModalConfirm';
import Notification from 'app/components/general/Notification';

const statuses = [
  {
    id: 'active',
    name: 'Active',
  },
  {
    id: 'inactive',
    name: 'Inactive',
  },
];

const formSchema = {
  type: 'object',
  required: ['name', 'friendlyCode', 'status', 'archived'],
  properties: {
    name: {
      type: 'string',
      title: 'Name',
    },
    friendlyCode: {
      type: 'string',
      title: 'Code',
      minLength: 2,
    },
    seasonName: {
      type: 'string',
      title: 'Season',
    },
    seasonStartAt: {
      type: 'array',
      items: {
        type: 'string',
      },
      enableTime: false,
      inputType: 'datetime',
      title: 'Start Date',
    },
    seasonEndAt: {
      type: 'array',
      items: {
        type: 'string',
      },
      enableTime: false,
      inputType: 'datetime',
      title: 'End Date',
    },
    externalSystemName: {
      type: 'string',
      title: 'Competition Name (In fixture system)',
    },
    status: {
      type: 'object',
      title: 'Status',
      options: statuses,
      inputType: 'select',
    },
    archived: {
      type: 'boolean',
      title: 'Archived',
      inputType: 'boolean',
    },
  },
};

const uiSchema = {
  status: { 'ui:field': 'select', classNames: 'col' },
  seasonStartAt: { 'ui:field': 'datetime' },
  seasonEndAt: { 'ui:field': 'datetime' },
  archived: { 'ui:field': 'boolean' },
};

interface ICompetitionDetailProps {
  appReady: boolean;
  actions: any;
  match: any;
  currentCompetition: any;
  currentCompetitionId: string;
  organisationFees: any;
  organisations: any;
  competitions: any;
  notificationConfigs: any;
  isLoading: boolean;
  errors: any;
  updateCompetition: any;
}

interface ICompetitionDetailState {
  formData: any;
  originalFormData: any;
  changed: boolean;
  saved: boolean;
  resetTeamConnectionsModalOpen: boolean;
  resetTeamConnectionsCount: number;
  errors: any;
}

const selector = (state): any => ({
  currentCompetition: competitionSelectors.getCurrentFormCompetition(state),
  currentCompetitionId: state.competitions.currentCompetitionId,
  competitions: competitionSelectors.getCompetitions(state),
  organisations: organisationSelectors.getOrganisations(state),
  notificationConfigs: notificationConfigsSelectors.getNotificationConfigs(
    state,
  ),
  organisationFees: organisationFeesSelectors.getOrganisationFees(state),
  isLoading: state.competitions.isLoading,
  appReady: loadingSelectors.appReady(state),
  errors: {
    ...state.competitions.backendErrors,
  },
});

const actionCreators = _.merge(
  competitionActions,
  organisationFeesActions,
  notificationConfigActions,
);

@connect(selector, (dispatch) => ({
  actions: bindActionCreators(actionCreators, dispatch),
}))
export default class CompetitionDetail extends React.Component<
  ICompetitionDetailProps,
  ICompetitionDetailState
> {
  static defaultProps: ICompetitionDetailProps = {
    appReady: false,
    actions: undefined,
    match: undefined,
    currentCompetition: {},
    currentCompetitionId: undefined,
    organisationFees: undefined,
    organisations: undefined,
    competitions: undefined,
    notificationConfigs: undefined,
    updateCompetition: undefined,
    isLoading: false,
    errors: undefined,
  };

  state: ICompetitionDetailState = {
    changed: false,
    saved: false,
    formData: {},
    originalFormData: {},
    errors: {},
    resetTeamConnectionsModalOpen: false,
    resetTeamConnectionsCount: undefined,
  };

  componentDidMount(): void {
    this.props.actions.getCompetition(this.props.match.params.competitionId);
  }

  static getDerivedStateFromProps(nextProps, prevState): any {
    if (!prevState.changed) {
      const status = _.find(statuses, {
        id: nextProps.currentCompetition.status,
      });
      const formData = {
        name: nextProps.currentCompetition.name || '',
        seasonName: nextProps.currentCompetition.seasonName || '',
        externalSystemName: nextProps.currentCompetition.externalSystemName || '',
        friendlyCode: nextProps.currentCompetition.friendlyCode || '',
        seasonStartAt: [nextProps.currentCompetition.seasonStartAt] || [],
        seasonEndAt: [nextProps.currentCompetition.seasonEndAt] || [],
        status: status || null,
        archived: nextProps.currentCompetition.archived || false,

      };
      return {
        ...prevState,
        formData,
        originalFormData: formData,
        errors: nextProps.errors,
      };
    }
    return null;
  }

  isValid = (): boolean => _.isEmpty(this.state.errors) && this.state.changed;

  handleChange = (e): void => {
    const changed = this.state.originalFormData !== e.formData;
    this.setState(() => ({
      formData: e.formData,
      errors: [],
      changed,
    }));
  };

  handleError = (errors): void => {
    this.setState({ errors });
  }

  handleSubmit = (e): void => {
    // console.log(e);
    // e.preventDefault();

    if (this.isValid()) {
      const payload = {
        id: this.props.match.params.competitionId,
        name: this.state.formData.name,
        seasonName: this.state.formData.seasonName,
        externalSystemName: this.state.formData.externalSystemName,
        friendlyCode: this.state.formData.friendlyCode,
        seasonStartAt: this.state.formData.seasonStartAt[0],
        seasonEndAt: this.state.formData.seasonEndAt[0],
        archived: this.state.formData.archived,
        status: !_.isEmpty(this.state.formData.status)
          ? this.state.formData.status.id
          : null,
      };
      this.props.actions.updateCompetition(payload);
    }
    this.setState({ changed: false });
  };

  handleOpenResetTeamConnectionsModal = (e): void => {
    e.preventDefault();
    this.setState({ resetTeamConnectionsModalOpen: true });
  };

  handleCloseResetTeamConnectionsModal = (e): void => {
    e.preventDefault();
    this.setState({ resetTeamConnectionsModalOpen: false });
  };

  buildActions = () => {
    const actions = [];

    actions.push(<Button
      className="btn-standard btn-small btn-2 margin-right-10 margin-bottom-10"
      noLoading
      onClick={this.handleOpenResetTeamConnectionsModal}
    >
      Reset team connections
    </Button>);
    return actions;
  }

  handleResetTeamConnections = (e): void => {
    e.preventDefault();
    const payload = {
      competition: {
        id: this.props.match.params.competitionId,
      },
    };
    this.props.actions.resetTeamConnections(payload, (response) => {
      this.setState({ resetTeamConnectionsModalOpen: false, resetTeamConnectionsCount: response.totalCount });
    });
  }

  render(): React.ReactNode {
    const competition: any = this.props.competitions.currentCompetition;
    const isLoading = this.props.competitions.isLoading && competition.id !== this.props.match.params.competitionId;
    const actions = this.buildActions();

    return (
      <div>
        <ModalConfirm
          title="Reset team connections"
          description="If the current season has ended and you want teams to connect to the next season, this will disconnect their current team connection and guide them to connect to the next season."
          buttonText="Confirm"
          isOpen={this.state.resetTeamConnectionsModalOpen}
          handleSubmit={this.handleResetTeamConnections}
          handleCancel={this.handleCloseResetTeamConnectionsModal}
        />
        <div className="row">
          <div className="col">
            <HeaderThin
              title={competition.name}
              isLoading={isLoading}
              breadcrumb="Competitions"
              goBack="/competitions"
              divider
            />
          </div>
        </div>
        <div className="row">
          <div className="col">
            <SectionGroup
              title="Details"
              isLoading={isLoading}
            >
              <SchemaForm
                schema={formSchema}
                uiSchema={uiSchema}
                handleChange={this.handleChange}
                handleError={this.handleError}
                handleSubmit={this.handleSubmit}
                formData={this.state.formData}
                isValid={this.isValid()}
                isLoading={this.props.competitions.isSaving}
                hasSaved={this.props.competitions.hasSaved}
                hasFailed={this.props.competitions.hasFailed}
                originalFormData={this.state.originalFormData}
              />
            </SectionGroup>
          </div>
        </div>
        <div className="row with-margin-top-extra-2">
          {actions.length > 0 && (
            <div className="col">
              <SectionGroup title="Actions" isLoading={isLoading}>
                <div className="">
                  {_.map(actions, (a) => a)}
                </div>
              </SectionGroup>
            </div>
          )}
        </div>
        {this.state.resetTeamConnectionsCount !== undefined && (
          <div className="row with-margin-top">
            <div className="col">
              <Notification handleClose={() => this.setState({ resetTeamConnectionsCount: undefined })}>
                <div className="d-inline-block">
                  {`${this.state.resetTeamConnectionsCount.toString()} team connections reset.`}
                </div>
              </Notification>
            </div>
          </div>
        )}
        <div className="row with-margin-top-extra-2">
          <div className="col">
            <OrganisationFeesTable
              {...this.props}
              {...this.props.organisationFees}
              headerTitle="Fees"
              persistentFilterConfig={{
                competitionId: this.props.match.params.competitionId,
                to: 'team-manager',
              }}
            />
          </div>
        </div>
        <div className="row with-margin-top-extra-2">
          <div className="col">
            <NotificationConfigsTable
              {...this.props}
              {...this.props.notificationConfigs}
              headerTitle="Notification Contacts"
              persistentFilterConfig={{
                competitionId: this.props.match.params.competitionId,
              }}
            />
          </div>
        </div>
      </div>
    );
  }
}
